import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import {
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
} from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DomSanitizer } from '@angular/platform-browser';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';

import { routes } from './app.routes';
import { msalInterceptor } from './core/providers/msal-interceptor';
import { registerAgChartsAndGrids } from './core/providers/register-ag-charts-grids';
import { ASSET_AGENT_RUNTIME_CONFIGURATION } from './core/providers/tokens';
import type { Configuration } from './shared/domain/configuration.model';
import { configureMatIconRegistry } from './shared/services/configure-mat-icon-registry';

let assetAgentRuntimeConfiguration: Configuration;

const loadRuntimeConfiguration = async () => {
  const response = await fetch('/api/public/config');
  assetAgentRuntimeConfiguration = (await response.json()) as Configuration;
  return assetAgentRuntimeConfiguration;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(async () => {
      configureMatIconRegistry(inject(MatIconRegistry), inject(DomSanitizer));
      const config = await loadRuntimeConfiguration();
      registerAgChartsAndGrids(config.agGridPublicKey);
    }),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptors([msalInterceptor]), withFetch()),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideLuxonDateAdapter(),
    {
      provide: ASSET_AGENT_RUNTIME_CONFIGURATION,
      useFactory: () => assetAgentRuntimeConfiguration,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: () => {
        const config = new MatDialogConfig();
        // prevents visual issue where the form resets before the exit animation finishes
        config.exitAnimationDuration = 0;
        return config;
      },
    },
  ],
};
